import { useState } from 'react'
import { Code2, Loader2, ExternalLink, X, CheckCircle, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  useGitHubStatus, useGitHubInitiate, useGitHubDisconnect,
  useGitHubRepos, useGitHubConnectPat,
} from '@/hooks/useApi'

export function GitHubIntegrationPanel() {
  const { data: status, isLoading: statusLoading } = useGitHubStatus()
  const { data: reposData, isLoading: reposLoading } = useGitHubRepos()
  const initiateMutation = useGitHubInitiate()
  const disconnectMutation = useGitHubDisconnect()
  const patMutation = useGitHubConnectPat()

  const [showPatForm, setShowPatForm] = useState(false)
  const [pat, setPat] = useState('')
  const [githubHost, setGithubHost] = useState('github.com')
  const [patError, setPatError] = useState('')
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false)
  const [connectError, setConnectError] = useState('')

  const connected = status?.connected ?? false
  const connectionType = status?.connection_type ?? null

  const handleConnect = async () => {
    setConnectError('')
    try {
      const result = await initiateMutation.mutateAsync()
      if (result.auth_url) window.location.href = result.auth_url
    } catch (e) {
      setConnectError(e instanceof Error ? e.message : 'Failed to initiate GitHub connection')
    }
  }

  const handleConnectPat = async () => {
    setPatError('')
    if (!pat.trim()) { setPatError('PAT required'); return }
    try {
      await patMutation.mutateAsync({
        pat: pat.trim(),
        githubHost: githubHost !== 'github.com' ? githubHost : undefined,
      })
      setPat('')
      setShowPatForm(false)
    } catch (e) {
      setPatError(e instanceof Error ? e.message : 'Failed to connect')
    }
  }

  const handleDisconnect = async () => {
    await disconnectMutation.mutateAsync().catch(() => null)
    setShowDisconnectConfirm(false)
  }

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-6 w-6 animate-spin text-text-muted" />
      </div>
    )
  }

  const repos = reposData?.repos ?? []

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-text-primary">GitHub Integration</h2>
        <p className="text-sm text-text-muted mt-0.5">
          Connect your GitHub account to enable repository-aware deployments.
        </p>
      </div>

      {connected ? (
        <>
          <Card className="p-4 bg-surface/30 border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Code2 className="h-5 w-5 text-text-muted" />
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-text-primary">
                      {status?.account_login ?? 'Connected'}
                    </span>
                    {connectionType === 'app' && <Badge variant="success" className="text-[10px]">GitHub App</Badge>}
                    {connectionType === 'pat' && <Badge variant="muted" className="text-[10px]">PAT</Badge>}
                  </div>
                  {status?.account_type && (
                    <p className="text-xs text-text-muted">{status.account_type}</p>
                  )}
                  {connectionType === 'pat' && status?.pat_hint && (
                    <p className="text-xs text-text-muted font-mono">{status.pat_hint}</p>
                  )}
                </div>
              </div>
              {showDisconnectConfirm ? (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-danger hover:bg-danger/10"
                    onClick={handleDisconnect}
                    disabled={disconnectMutation.isPending}
                  >
                    {disconnectMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                    Confirm
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowDisconnectConfirm(false)}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-text-muted hover:text-danger"
                  onClick={() => setShowDisconnectConfirm(true)}
                >
                  Disconnect
                </Button>
              )}
            </div>
            {showDisconnectConfirm && (
              <p className="text-xs text-warning mt-3">
                Deployments using GitHub integration will stop working. This cannot be undone without reconnecting.
              </p>
            )}
          </Card>

          {/* Repos */}
          <div>
            <h3 className="text-sm font-medium text-text-primary mb-3">
              Accessible repositories
              {status?.repos_access === 'selected' && (
                <span className="text-xs text-text-muted ml-2">(selected repos only)</span>
              )}
            </h3>
            {reposLoading ? (
              <div className="flex items-center gap-2 text-text-muted text-sm">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading repositories...
              </div>
            ) : repos.length === 0 ? (
              <p className="text-sm text-text-muted">No repositories found.</p>
            ) : (
              <div className="space-y-1.5 max-h-80 overflow-y-auto">
                {repos.slice(0, 50).map((repo) => (
                  <div
                    key={repo.full_name}
                    className="flex items-center gap-2 py-1.5 px-3 rounded-lg bg-surface/20 border border-border/50"
                  >
                    <Code2 className="h-3.5 w-3.5 text-text-muted shrink-0" />
                    <span className="text-sm text-text-primary flex-1 truncate">{repo.full_name}</span>
                    <span className="text-xs text-text-muted">{repo.default_branch}</span>
                    {repo.private && (
                      <span className="text-[10px] px-1 py-0.5 rounded bg-white/5 text-text-muted">private</span>
                    )}
                  </div>
                ))}
                {repos.length > 50 && (
                  <p className="text-xs text-text-muted px-3">+{repos.length - 50} more</p>
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="space-y-4">
          <Card className="p-6 bg-surface/30 border-border text-center">
            <Code2 className="h-10 w-10 mx-auto mb-3 text-text-muted opacity-40" />
            <h3 className="text-sm font-medium text-text-primary mb-1">Connect GitHub</h3>
            <p className="text-xs text-text-muted mb-4 max-w-xs mx-auto">
              Install the OpsBridge GitHub App to enable repository-aware deployments and CI/CD workflows.
            </p>
            <Button onClick={handleConnect} disabled={initiateMutation.isPending}>
              {initiateMutation.isPending
                ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                : <ExternalLink className="h-4 w-4 mr-2" />}
              Install GitHub App
            </Button>
            {connectError && (
              <p className="text-xs text-danger mt-2 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />{connectError}
              </p>
            )}
          </Card>

          <div>
            <button
              onClick={() => setShowPatForm(!showPatForm)}
              className="text-xs text-text-muted hover:text-text-primary underline underline-offset-2 transition-colors"
            >
              Or connect with a Personal Access Token (for GitHub Enterprise)
            </button>

            {showPatForm && (
              <Card className="p-4 mt-3 bg-surface/30 border-border space-y-3">
                <div className="flex items-start gap-2 text-xs text-warning">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>PAT connection has limited functionality. Recommended for self-hosted GitHub Enterprise only.</span>
                </div>
                <Input
                  type="password"
                  placeholder="ghp_... or ghs_... (Personal Access Token)"
                  value={pat}
                  onChange={(e) => setPat(e.target.value)}
                  className="bg-bg border-border text-text-primary font-mono text-sm"
                />
                <Input
                  placeholder="GitHub host (default: github.com)"
                  value={githubHost}
                  onChange={(e) => setGithubHost(e.target.value)}
                  className="bg-bg border-border text-text-primary text-sm"
                />
                {patError && (
                  <p className="text-xs text-danger flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />{patError}
                  </p>
                )}
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleConnectPat} disabled={patMutation.isPending}>
                    {patMutation.isPending
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                      : <CheckCircle className="h-3.5 w-3.5 mr-1.5" />}
                    Connect
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setShowPatForm(false); setPatError('') }}>
                    Cancel
                  </Button>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
